import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Grid,
    Paper,
    Stack,
    Text,
    Badge,
    Group,
    Title,
    Divider,
    Box,
    Loader,
    Alert,
    Button,
    Textarea,
    ScrollArea,
    ThemeIcon,
    Breadcrumbs,
    Anchor,
    TextInput,
    Select
} from '@mantine/core';
import {
    IconSettings,
    IconAlertCircle,
    IconDeviceFloppy,
    IconChevronRight,
    IconInfoCircle,
    IconCheck,
    IconX,
    IconEdit,
    IconFileText,
    IconCategory
} from '@tabler/icons-react';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import useAuthenticatedPut from '../../hooks/useAuthenticatedPut';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import config from '../../config';

const FeatureEditor = () => {
    const [searchParams] = useSearchParams();
    const featureKey = searchParams.get('featureKey');

    const [featureName, setFeatureName] = useState('');
    const [featureId, setFeatureId] = useState('');
    const [featureDescription, setFeatureDescription] = useState('');
    const [featureType, setFeatureType] = useState('');
    const [requirements, setRequirements] = useState('');
    const [featureMetadata, setFeatureMetadata] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null);

    const { put } = useAuthenticatedPut();

    // counter to bust cache after updates
    const [reloadCounter, setReloadCounter] = useState(0);

    const { post, isLoading: isCreating } = useAuthenticatedPost();

    // main feature fetch
    const featureUrl = `${config.apiBaseUrl}/queries/features/${encodeURIComponent(featureKey)}`;
    const {
        data: featureData,
        isLoading: loading,
        error,
        refetch,
    } = useAuthenticatedData(featureUrl);

    // seed feature data
    useEffect(() => {
        if (featureData) {
            setFeatureId(featureData.id || '');
            setFeatureName(featureData.name || '');
            setFeatureDescription(featureData.description || '');
            setFeatureType(featureData.featureType || '');
            setRequirements(featureData.requirements || '');
            setFeatureMetadata(featureData.metadata || {});
            setBreadcrumbs(featureData.breadcrumbs || []);
        }
    }, [featureData]);

    // auto-create on 404
    useEffect(() => {
        if (featureKey && error?.status === 404 && !isCreating) {
            post(
                `${config.apiBaseUrl}/queries/features/new-default?featureKey=${featureKey}`,
            )
                .then(() => {
                    if (refetch) refetch();
                    setReloadCounter((c) => c + 1);
                })
                .catch(console.error);
        }
    }, [error, featureKey, post, refetch, isCreating]);

    const handleSave = async () => {
        setIsSaving(true);
        setSaveResult(null);
        try {
            const result = await put(
                `${config.apiBaseUrl}/queries/features/${encodeURIComponent(featureKey)}`,
                {
                    id: featureId,
                    name: featureName,
                    description: featureDescription,
                    featureType: featureType,
                    requirements: requirements
                }
            );

            setSaveResult({
                hasError: result.hasError,
                message: result.message,
                validationErrors: result.validationErrors
            });

            // clear after a bit
            setTimeout(() => setSaveResult(null), 5000);

            // refresh data if save was successful
            if (!result.hasError && refetch) {
                refetch();
                setReloadCounter((c) => c + 1);
            }
        }
        catch (err) {
            console.error('Save error:', err);
            setSaveResult({ hasError: true, message: err.message || 'Save failed' });
        } finally {
            setIsSaving(false);
        }
    };

    const featureTypeOptions = [
        { value: 'KPI', label: 'KPI (Key Performance Indicator)' },
        { value: 'Scorecard', label: 'Scorecard' }
    ];

    const isFormValid =
        (featureName || '').trim() &&
        (featureDescription || '').trim() &&
        featureType;

    // early guards
    if (!featureKey) {
        return (
            <Container size="xl" py="md">
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                    <Text>No feature ID provided. Please access this page from the Query Commander explorer.</Text>
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container size="xl" py="md">
                <Paper p="md">
                    <Group gap="xs">
                        <Loader size="sm" />
                        <Text>Loading Feature...</Text>
                    </Group>
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container size="xl" py="md">
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                    Error loading feature: {error.message || String(error)}
                </Alert>
            </Container>
        );
    }

    if (!featureData) {
        return (
            <Container size="xl" py="md">
                <Paper p="md">
                    <Text>No feature data available</Text>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="xl" py="md">
            <Stack gap="md">
                <Paper p="md" withBorder>
                    <Stack gap="md">
                        <Group justify="space-between">
                            <Group gap="xs">
                                <IconSettings size={24} />
                                <Title order={2}>{featureName}</Title>
                                <Badge color="purple" variant="light">Feature Key: {featureKey}</Badge>
                                {featureType && <Badge color="blue" variant="light">{featureType}</Badge>}
                            </Group>
                            <Group gap="xs">
                                <Button
                                    leftSection={<IconDeviceFloppy size={16} />}
                                    onClick={handleSave}
                                    loading={isSaving}
                                    disabled={!isFormValid}
                                    color={saveResult?.hasError === false ? 'green' : saveResult?.hasError === true ? 'red' : 'blue'}
                                >
                                    {saveResult?.hasError === false ? 'Saved!' : saveResult?.hasError === true ? 'Error' : 'Save Feature'}
                                </Button>
                            </Group>
                        </Group>

                        {breadcrumbs.length > 0 && (
                            <Box>
                                <Text size="sm" fw={500} mb="xs">Location:</Text>
                                <Breadcrumbs separator={<IconChevronRight size={12} />}>
                                    {breadcrumbs.map((crumb, i) => (
                                        <Anchor key={i} size="sm" c="dimmed">{crumb}</Anchor>
                                    ))}
                                </Breadcrumbs>
                            </Box>
                        )}

                        {/* Save result alert */}
                        {saveResult && (
                            <Alert
                                icon={saveResult.hasError ? <IconX size={16} /> : <IconCheck size={16} />}
                                color={saveResult.hasError ? 'red' : 'green'}
                                variant="light"
                            >
                                <Text fw={500} size="sm" c={saveResult.hasError ? 'red' : 'green'}>
                                    {saveResult.message}
                                </Text>
                                {saveResult.validationErrors?.length > 0 && (
                                    <Stack gap={2} mt="xs">
                                        {saveResult.validationErrors.map((err, i) => (
                                            <Text key={i} size="sm" c="red">• {err}</Text>
                                        ))}
                                    </Stack>
                                )}
                            </Alert>
                        )}
                    </Stack>
                </Paper>

                <Grid>
                    <Grid.Col span={12}>
                        <Paper p="md" withBorder>
                            <Stack gap="md">
                                <Group gap="xs">
                                    <IconInfoCircle size={20} />
                                    <Title order={4}>Feature Description</Title>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Describe what this feature does and its purpose. This will be the main description shown to users.
                                </Text>
                                <Divider />

                                <Textarea
                                    value={featureDescription}
                                    onChange={(e) => setFeatureDescription(e.currentTarget.value)}
                                    placeholder="Describe what this feature does and its purpose..."
                                    minRows={8}
                                    maxRows={12}
                                    required
                                    size="md"
                                />
                            </Stack>
                        </Paper>
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Paper p="md" withBorder>
                            <Stack gap="md">
                                <Group gap="xs">
                                    <IconCategory size={20} />
                                    <Title order={4}>Feature Type</Title>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Select the type of feature this represents.
                                </Text>
                                <Divider />

                                <Select
                                    value={featureType}
                                    onChange={setFeatureType}
                                    data={featureTypeOptions}
                                    placeholder="Select feature type..."
                                    required
                                    size="md"
                                    clearable
                                />
                            </Stack>
                        </Paper>
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Paper p="md" withBorder>
                            <Stack gap="md">
                                <Group gap="xs">
                                    <IconFileText size={20} />
                                    <Title order={4}>Requirements</Title>
                                </Group>
                                <Text size="xs" c="dimmed">
                                    Define the technical requirements and specifications for this feature. This can include business rules,
                                    calculation methods, data sources, or any other relevant information.
                                </Text>
                                <Divider />

                                <Textarea
                                    value={requirements}
                                    onChange={(e) => setRequirements(e.currentTarget.value)}
                                    placeholder="Enter feature requirements, business rules, calculations, data sources, etc..."
                                    minRows={15}
                                    maxRows={20}
                                    style={{ fontFamily: 'monospace' }}
                                />
                            </Stack>
                        </Paper>
                    </Grid.Col>
                </Grid>

                <Paper p="md" withBorder>
                    <Stack gap="md">
                        <Group gap="xs">
                            <IconCategory size={20} />
                            <Title order={4}>Feature Summary</Title>
                        </Group>

                        

                        <Grid>
                            <Grid.Col span={6}>
                                <Group gap="xs" align="flex-start">
                                    <Text fw={500} size="sm">Name:</Text>
                                    <Text size="sm" c={featureName.trim() ? 'inherit' : 'dimmed'} style={{ flex: 1 }}>
                                        {featureName.trim() || 'Not specified'}
                                    </Text>
                                </Group>
                            </Grid.Col>

                            <Grid.Col span={6}>
                                <Group gap="xs" align="flex-start">
                                    <Text fw={500} size="sm">Id:</Text>
                                    <Text size="sm" c={featureId ? 'inherit' : 'dimmed'} style={{ flex: 1 }}>
                                        {featureId || 'Not specified'}
                                    </Text>
                                </Group>
                            </Grid.Col>

                            <Grid.Col span={6}>
                                <Group gap="xs" align="flex-start">
                                    <Text fw={500} size="sm">Type:</Text>
                                    <Text size="sm" c={featureType ? 'inherit' : 'dimmed'} style={{ flex: 1 }}>
                                        {featureType || 'Not specified'}
                                    </Text>
                                </Group>
                            </Grid.Col>

                            <Grid.Col span={12}>
                                <Group gap="xs" align="flex-start">
                                    <Text fw={500} size="sm">Description:</Text>
                                    <Text size="sm" c={featureDescription.trim() ? 'inherit' : 'dimmed'} style={{ flex: 1 }}>
                                        {featureDescription.trim() || 'No description provided'}
                                    </Text>
                                </Group>
                            </Grid.Col>

                            <Grid.Col span={12}>
                                <Group gap="xs" align="flex-start">
                                    <Text fw={500} size="sm">Requirements:</Text>
                                    <Text size="sm" c={requirements.trim() ? 'inherit' : 'dimmed'} style={{ flex: 1 }}>
                                        {requirements.trim() ?
                                            (requirements.length > 200 ? requirements.substring(0, 200) + '...' : requirements) :
                                            'No requirements specified'
                                        }
                                    </Text>
                                </Group>
                            </Grid.Col>
                        </Grid>
                    </Stack>
                </Paper>
            </Stack>
        </Container>
    );
};

export default FeatureEditor;